/**
 * Trunnion AI - Command Palette Item
 * A single selectable row within the command palette results list.
 */

import { useRef, useEffect } from 'react';
import { type LucideIcon } from 'lucide-react';

export interface Command {
  id: string;
  label: string;
  icon: LucideIcon;
  section: 'Navigation' | 'Actions' | 'Recent';
  shortcut?: string;
  action: () => void;
}

interface CommandItemProps {
  command: Command;
  isSelected: boolean;
  onSelect: () => void;
  onHover: () => void;
}

export function CommandItem({ command, isSelected, onSelect, onHover }: CommandItemProps) {
  const ref = useRef<HTMLButtonElement>(null);
  const Icon = command.icon;

  useEffect(() => {
    if (isSelected && ref.current) {
      ref.current.scrollIntoView({ block: 'nearest' });
    }
  }, [isSelected]);

  return (
    <button
      ref={ref}
      type="button"
      role="option"
      aria-selected={isSelected}
      className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm transition-colors outline-none ${
        isSelected
          ? 'bg-trunnion-gold-500/10 text-trunnion-gold-500'
          : 'text-trunnion-slate-300 hover:bg-trunnion-slate-700/50'
      }`}
      onMouseEnter={onHover}
      onClick={onSelect}
    >
      <Icon className="h-4 w-4 shrink-0" />

      <span className="flex-1 truncate">{command.label}</span>

      {command.shortcut && (
        <kbd className="ml-auto shrink-0 rounded bg-trunnion-slate-700 border border-trunnion-slate-600 px-1.5 py-0.5 text-xs font-mono text-trunnion-slate-400">
          {command.shortcut}
        </kbd>
      )}
    </button>
  );
}
